import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import Reveal from '../components/Reveal';
import IPhone from '../components/IPhone';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import StoreButtons from '../components/StoreButtons';
import { LineIcon, BrandIcon } from '../components/icons';
import { usePressSpring } from '../hooks/usePressSpring';
import { byId } from '../data/apps';
import { STUDIO } from '../data/studio';
import { useLanguage } from '../i18n/LanguageContext';
import { cx } from '../utils/cx';
import mixins from '../styles/mixins.module.css';
import styles from './AppDetailPage.module.css';
import NotFound from './NotFound';

function BackLink() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const ref = usePressSpring(mixins.pressed);
  return (
    <button ref={ref} className={cx(mixins.back, mixins.glass, mixins.spring, styles.back)} onClick={() => navigate('/apps')}>
      <LineIcon name="chev" strokeWidth={2.6} /> {t('backToApps')}
    </button>
  );
}

function Thumbs({ screenshots, name, active, onSelect, landscape }) {
  return (
    <div className={cx(styles.thumbs, landscape && styles.thumbsLand)}>
      {screenshots.map((src, i) => (
        <button key={src} className={cx(i === active && styles.thumbOn)} onClick={() => onSelect(i)}>
          <img src={src} alt={`${name} screenshot ${i + 1}`} loading="lazy" />
        </button>
      ))}
    </div>
  );
}

export default function AppDetailPage({ appId: appIdProp }) {
  const { t, L } = useLanguage();
  const params = useParams();
  const appId = appIdProp ?? params.appId;
  const app = byId(appId);
  const carouselRef = useRef(null);
  const [activeShot, setActiveShot] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [appId]);

  useEffect(() => {
    document.title = app ? `${app.fullName} — OnyxUp` : 'OnyxUp';
  }, [app]);

  if (!app) return <NotFound />;

  const landscape = app.orientation === 'landscape';
  const hasShots = app.screenshots && app.screenshots.length > 0;

  const phone = (
    <IPhone landscape={landscape} big={!landscape} id="detailPhone">
      {hasShots ? (
        <ScreenshotCarousel
          ref={carouselRef}
          screenshots={app.screenshots}
          name={app.name}
          frameLand={landscape}
          onIndexChange={setActiveShot}
          dots={false}
        />
      ) : null}
    </IPhone>
  );

  const thumbs = hasShots ? (
    <Thumbs
      screenshots={app.screenshots}
      name={app.name}
      active={activeShot}
      landscape={landscape}
      onSelect={(i) => carouselRef.current && carouselRef.current.goTo(i)}
    />
  ) : null;

  const aboutCard = (
    <Reveal>
      <GlassCard as="div" className={mixins.card}>
        <h2>{t('aboutTitle')}</h2>
        {L(app.description).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </GlassCard>
    </Reveal>
  );

  const legalCard = (
    <Reveal delay={80}>
      <GlassCard as="div" className={mixins.card}>
        <h2>{t('legalSupportTitle')}</h2>
        <div className={mixins.legalList}>
          <Link to={`/app/${app.id}/privacy`}>
            <span className={mixins.li}><LineIcon name="shield" /></span>
            {t('privacyPolicy')}
            <span className={mixins.chev}><LineIcon name="chevR" strokeWidth={2.4} /></span>
          </Link>
          <Link to={`/app/${app.id}/terms`}>
            <span className={mixins.li}><LineIcon name="doc" /></span>
            {t('termsOfUse')}
            <span className={mixins.chev}><LineIcon name="chevR" strokeWidth={2.4} /></span>
          </Link>
          {app.instagram && (
            <a href={app.instagram} target="_blank" rel="noopener noreferrer">
              <span className={mixins.li}><BrandIcon name="instagram" /></span>
              {t('instagram')}
              <span className={mixins.chev}><LineIcon name="chevR" strokeWidth={2.4} /></span>
            </a>
          )}
          <a href={`mailto:${STUDIO.email}?subject=${encodeURIComponent(app.name + ' Support')}`}>
            <span className={mixins.li}><LineIcon name="support" /></span>
            {t('support')}
            <span className={mixins.chev}><LineIcon name="chevR" strokeWidth={2.4} /></span>
          </a>
        </div>
      </GlassCard>
    </Reveal>
  );

  return (
    <section className={cx(styles.detail, mixins.wrap)}>
      <BackLink />

      <Reveal>
        <GlassCard as="div" className={styles.dHero}>
          {app.art && (
            <picture className={styles.heroArt}>
              <source media="(max-width: 700px)" srcSet={app.art.mobileWebp || app.art.mobile} type={app.art.mobileWebp ? 'image/webp' : undefined} />
              {app.art.mobileWebp && <source media="(max-width: 700px)" srcSet={app.art.mobile} />}
              {app.art.desktopWebp && <source srcSet={app.art.desktopWebp} type="image/webp" />}
              <img className={styles.heroArtImg} src={app.art.desktop} alt="" loading="lazy" />
            </picture>
          )}
          <div className={styles.tint} style={{ background: `radial-gradient(circle, ${app.tint}, transparent 65%)` }} />
          <div className={cx(mixins.appIcon, styles.heroIcon)}>
            {app.icon ? <img src={app.icon} alt={app.name} /> : null}
          </div>
          <div>
            <h1 className={styles.dTitle}>{app.fullName}</h1>
            <p className={styles.dSub}>{L(app.subtitle)}</p>
            <div className={styles.row}>
              <StoreButtons app={app} />
              <nav className={mixins.miniLegal}>
                <Link to={`/app/${app.id}/privacy`}>{t('privacyPolicy')}</Link>
                <Link to={`/app/${app.id}/terms`}>{t('termsOfUse')}</Link>
                {app.instagram && (
                  <a className={mixins.ig} href={app.instagram} target="_blank" rel="noopener noreferrer">
                    <BrandIcon name="instagram" /> {t('instagram')}
                  </a>
                )}
              </nav>
            </div>
          </div>
        </GlassCard>
      </Reveal>

      {landscape ? (
        <>
          <Reveal delay={100}>
            <div className={styles.dLand}>
              <div className={cx(mixins.halo, styles.landHalo)} />
              {phone}
              {thumbs}
            </div>
          </Reveal>
          <div className={styles.dCols}>
            {aboutCard}
            {legalCard}
          </div>
        </>
      ) : (
        <div className={styles.dGrid}>
          <Reveal delay={100}>
            <div className={styles.dPhone}>
              <div className={cx(mixins.halo, styles.phoneHalo)} />
              {phone}
              {thumbs}
            </div>
          </Reveal>
          <div className={styles.dCol}>
            {aboutCard}
            {legalCard}
          </div>
        </div>
      )}
    </section>
  );
}
